'use client';

import { CacheKey } from '@eliadmin/core/src/api/constants';
import { updateCustomer } from '@eliadmin/core/src/api/customers/customers-api';
import { ValidationError } from '@eliadmin/core/src/api/errors';
import {
  fetchCountries,
  fetchGenders,
  uploadFile,
} from '@eliadmin/core/src/api/general/general-api';
import { Button } from '@eliadmin/core/src/components/ui/button';
import {
  CollapsibleContent,
  CollapsibleTrigger,
} from '@eliadmin/core/src/components/ui/collapsible';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@eliadmin/core/src/components/ui/form';
import type { InputProps } from '@eliadmin/core/src/components/ui/input';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { Label } from '@eliadmin/core/src/components/ui/label';
import { PhoneInputField } from '@eliadmin/core/src/components/ui/phone-input/phone-input';
import { RadioGroup, RadioGroupItem } from '@eliadmin/core/src/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@eliadmin/core/src/components/ui/select';
import { Separator } from '@eliadmin/core/src/components/ui/separator';
import { useFormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { Collapsible } from '@radix-ui/react-collapsible';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import { useMemo, useRef, useState } from 'react';
import type { Control, FieldPath } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { FiChevronDown } from 'react-icons/fi';
import { z } from 'zod';
import type { FormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { useToast } from '@eliadmin/core/src/components/ui/use-toast';
import { useRouter } from 'next/navigation';
type ProfileEditFormValues = z.infer<ReturnType<typeof profileEditSchema>>;
export function ProfileEditForm() {
  const customer = useCustomerStore(s => s.me!.customer);
  const authToken = useCustomerStore(s => s.me!.authToken);
  const { toast } = useToast();

  const ref = useRef<HTMLInputElement>(null);

  const [file, setFile] = useState<File | undefined>(undefined);

  const locale = useLocaleStore(s => s.currentLocale);
  const router = useRouter();

  const { data: countries = { data: [] }, isLoading: isCountriesLoading } = useQuery({
    queryKey: [CacheKey.COUNTRIES, locale],
    queryFn: () => fetchCountries({ locale }),
    refetchOnWindowFocus: false,
  });

  const { data: genders = { data: [] }, isLoading: isGendersLoading } = useQuery({
    queryKey: [CacheKey.GENDERS, locale],
    queryFn: () => fetchGenders({ locale }),
    refetchOnWindowFocus: false,
  });

  const { t } = useTranslations();

  const { formValidationMessages } = useFormValidationMessages(t);

  const form = useForm<ProfileEditFormValues>({
    resolver: zodResolver(profileEditSchema(formValidationMessages)),
    defaultValues: {
      name: customer?.name ?? '',
      surname: customer?.surname ?? '',
      email: customer?.email ?? '',
      phone: customer?.phone ?? '',
      gender: customer?.gender ?? undefined,
      address: customer?.address ?? '',
      street_number: customer?.street_number ?? '',
      city: customer?.city ?? '',
      postcode: customer?.postcode ?? '',
      country: customer?.country ?? undefined,
      iban: customer?.iban ?? '',
      swift: customer?.swift ?? '',
      address_delivery: customer?.address_delivery ?? '',
      street_number_delivery: customer?.street_number_delivery ?? '',
      postcode_delivery: customer?.postcode_delivery ?? '',
      city_delivery: customer?.city_delivery ?? '',
      country_delivery: customer?.country_delivery ?? undefined,
      company: customer?.company ?? '',
      ico: customer?.ico ?? '',
      dic: customer?.dic ?? '',
      ic_dph: customer?.ic_dph ?? '',
    },
  });

  const [isOpenInvoiceAddress, setIsOpenInvoiceAddress] = useState(false);
  const [isOpenCompanyData, setIsOpenCompanyData] = useState(false);

  const { mutateAsync } = useMutation({
    mutationFn: updateCustomer,
  });

  const addressAsInvoiceAddress = () => {
    form.setValue('address_delivery', form.getValues('address'));
    form.setValue('street_number_delivery', form.getValues('street_number'));
    form.setValue('postcode_delivery', form.getValues('city'));
    form.setValue('city_delivery', form.getValues('postcode'));
    form.setValue('country_delivery', form.getValues('country') as any);
    return;
  };

  const handleSubmit = form.handleSubmit(async data => {
    if (file) {
      const formData = new FormData();
      formData.append('file', file);

      try {
        const res = await uploadFile({
          authToken,
          formData,
          locale,
        });
        data.image = res.data;
      } catch (e) {
        //TODO: handle error / create custom field for this error
        return;
      }
    }

    try {
      await mutateAsync({
        locale,
        data: data as any,
        authToken,
      });

      router.refresh();

      toast({
        title: t('Vaše údaje boli úspešne aktualizované'),
        variant: 'success',
      });
    } catch (e) {
      if (e instanceof ValidationError) {
        for (const [field, errors] of Object.entries(e.fields)) {
          form.setError(field as any, { type: 'manual', message: errors.join('\n') });
        }
        toast({
          title: t('Pri aktualizovaní vaších údajov nastala chyba'),
          description: form.formState.errors.toString(),
          variant: 'destructive',
        });
        return;
      }
    }
  });

  const handlePickFile = () => {
    ref.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    if (!file) {
      setFile(undefined);
      return;
    }

    setFile(file);
  };

  return (
    <Form {...form}>
      <form className="grid grid-cols-12 gap-4" onSubmit={handleSubmit}>
        <div className={cn('col-span-12 flex flex-col  gap-4 py-5 md:flex-row')}>
          <Image
            src={
              file ? URL.createObjectURL(file) : (customer.image?.url ?? '/images/placeholder.png')
            }
            alt="Avatar"
            width="0"
            height="0"
            sizes="100vw"
            className={cn(
              'h-auto w-[187px] rounded-lg object-contain',
              'ea-profile-edit__user-image',
            )}
          />
          <div className={cn('flex flex-col items-start justify-start gap-4')}>
            <div className={cn('flex flex-col items-start  gap-1')}>
              <h5 className={cn('text-2xl font-bold')}>
                {customer.name} {customer.surname}
              </h5>
              <p className={cn('text-sm text-muted-foreground')}>{customer.email}</p>
            </div>
            <input type="file" ref={ref} className={cn('hidden')} onChange={handleFileChange} />
            <Button className="w-full md:w-auto" variant="secondary" onClick={handlePickFile}>
              {t('text250')}
            </Button>
          </div>
        </div>

        {/* personal data */}
        <SectionHeader title={t('text198')} />
        <Field
          required
          control={form.control}
          name="name"
          label={t('text33')}
          placeholder={t('text33')}
        />
        <Field
          required
          control={form.control}
          name="surname"
          label={t('text34')}
          placeholder={t('text34')}
        />
        <Field
          required
          control={form.control}
          name="email"
          type="email"
          label={t('text35')}
          placeholder={t('text35')}
        />
        <Field
          control={form.control}
          name="phone"
          type="tel"
          label={t('text36')}
          placeholder={t('text36')}
        />
        <Field
          control={form.control}
          name="gender.id"
          type="radio"
          label={t('text255')}
          isDisabled={isGendersLoading}
          options={genders.data.map(g => ({
            value: g.id,
            label: g.name,
          }))}
          placeholder={t('text252')}
        />

        {/* address */}
        <SectionHeader title={t('text157')} />
        <Field
          control={form.control}
          name="address"
          label={t('text38')}
          placeholder={t('text38')}
        />
        <Field
          control={form.control}
          name="street_number"
          label={t('text438')}
          placeholder={t('text438')}
        />
        <Field control={form.control} name="city" label={t('text39')} placeholder={t('text39')} />
        <Field
          control={form.control}
          name="postcode"
          label={t('text40')}
          placeholder={t('text40')}
        />
        {/* TODO: countries */}
        <Field
          control={form.control}
          type="select"
          name="country.id"
          label={t('text519')}
          placeholder={t('text519')}
          isDisabled={isCountriesLoading}
          options={countries.data.map(c => ({
            value: c.id,
            label: c.name,
          }))}
        />
        {/* billing */}
        <SectionHeader title={t('text293')} />
        <Field control={form.control} name="iban" label={t('text294')} placeholder={t('text294')} />
        <Field
          control={form.control}
          name="swift"
          label={t('text295')}
          placeholder={t('text295')}
        />
        <SectionHeader title="" />
        <Collapsible
          open={isOpenInvoiceAddress}
          onOpenChange={() => setIsOpenInvoiceAddress(!isOpenInvoiceAddress)}
          className="col-span-12"
        >
          <CollapsibleTrigger className="flex w-full justify-between">
            <h3 className="text-xl font-semibold text-foreground">{t('text359')}</h3>
            <span className={cn('text-primary transition-transform duration-100 ease-linear')}>
              <FiChevronDown
                className={cn('h-5 w-5 text-primary', isOpenInvoiceAddress && 'rotate-180')}
              />
            </span>
          </CollapsibleTrigger>
          <CollapsibleContent className="ml-0 mt-2">
            <div className="grid grid-cols-1 gap-2 md:grid-cols-12">
              <div className="md:col-span-12">
                <Button className="h-10" onClick={() => addressAsInvoiceAddress()} type="button">
                  Použiť adresu
                </Button>
              </div>
              <div className="md:col-span-6">
                <Field
                  control={form.control}
                  name="address_delivery"
                  label={t('text38')}
                  placeholder={t('text38')}
                  required
                />
              </div>
              <div className="md:col-span-6">
                <Field
                  control={form.control}
                  name="street_number_delivery"
                  label={t('text438')}
                  placeholder={t('text438')}
                  required
                />
              </div>
              <div className="min-w-[80px] md:col-span-3">
                <Field
                  control={form.control}
                  name="postcode_delivery"
                  label={t('text40')}
                  placeholder={t('text40')}
                  required
                />
              </div>
              <div className="md:col-span-9">
                <Field
                  control={form.control}
                  name="city_delivery"
                  label={t('text39')}
                  placeholder={t('text39')}
                  required
                />
              </div>
              <div className="md:col-span-12">
                <Field
                  control={form.control}
                  name="country_delivery.id"
                  type="select"
                  label={t('text41')}
                  placeholder={t('text41')}
                  options={countries.data.map(c => ({ value: c.id, label: t(c.name) }))}
                  required
                />
              </div>
            </div>
          </CollapsibleContent>
        </Collapsible>
        <SectionHeader title="" />
        <Collapsible
          open={isOpenCompanyData}
          onOpenChange={() => setIsOpenCompanyData(!isOpenCompanyData)}
          className="col-span-12"
        >
          <CollapsibleTrigger className="flex w-full justify-between">
            <h3 className="text-xl font-semibold text-foreground">{t('text689')}</h3>
            <span className={cn('text-primary transition-transform duration-100 ease-linear')}>
              <FiChevronDown
                className={cn('h-5 w-5 text-primary', isOpenCompanyData && 'rotate-180')}
              />
            </span>
          </CollapsibleTrigger>
          <CollapsibleContent className="ml-0 mt-2">
            <div className="grid grid-cols-1 gap-2 md:grid-cols-12">
              <div className="md:col-span-6">
                <Field
                  control={form.control}
                  name="company"
                  label={t('text37')}
                  placeholder={t('text37')}
                  required
                />
              </div>
              <div className="md:col-span-6">
                <Field
                  control={form.control}
                  name="ico"
                  label={t('text42')}
                  placeholder={t('text42')}
                  required
                />
              </div>
              <div className="md:col-span-6">
                <Field
                  control={form.control}
                  name="dic"
                  label={t('text43')}
                  placeholder={t('text43')}
                  required
                />
              </div>
              <div className="md:col-span-6">
                <Field
                  control={form.control}
                  name="ic_dph"
                  label={t('text44')}
                  placeholder={t('text44')}
                />
              </div>
            </div>
          </CollapsibleContent>
        </Collapsible>

        <div className="col-span-12 flex flex-row justify-end">
          <Button
            type="submit"
            className="w-full md:w-auto"
            isLoading={form.formState.isSubmitting}
          >
            {t('text201')}
          </Button>
        </div>
      </form>
    </Form>
  );
}

function SectionHeader({ title }: { title: string }) {
  return (
    <>
      <div className="col-span-12 pt-4">
        <Separator />
      </div>
      <div className="col-span-12">
        <h4 className="text-xl font-semibold text-foreground">{title}</h4>
      </div>
    </>
  );
}

type FieldProps = {
  control: Control<ProfileEditFormValues>;
  name: FieldPath<ProfileEditFormValues>;
  placeholder?: string;
  label?: string;
  required?: boolean;
  type?: InputProps['type'] | 'tel' | 'radio';
  options?: { value: string | number; label: string }[];
  isFullWidth?: boolean;
  isDisabled?: boolean;
};

function Field(props: FieldProps) {
  const content = useMemo(() => {
    if (props.type === 'select') {
      if (!props.options) {
        throw new Error('Select type requires options prop');
      }

      return (
        <FormField
          control={props.control}
          name={props.name}
          render={({ field }) => {
            return (
              <FormItem required={props.required} className="p-[1px]">
                <FormLabel>{props.label}</FormLabel>
                <FormControl>
                  <Select
                    value={String(field.value)}
                    onValueChange={field.onChange}
                    disabled={props.isDisabled}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={props.placeholder} />
                    </SelectTrigger>
                    <SelectContent>
                      {props.options?.map(o => (
                        <SelectItem key={o.value} value={String(o.value)}>
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>
            );
          }}
        />
      );
    }

    if (props.type === 'radio') {
      if (!props.options) {
        throw new Error('Radio type requires options prop');
      }

      return (
        <FormField
          control={props.control}
          name={props.name}
          render={({ field }) => {
            return (
              <FormItem required={props.required} className="flex flex-col gap-2 p-[1px]">
                <FormLabel>{props.label}</FormLabel>
                <FormControl>
                  <RadioGroup
                    value={String(field.value)}
                    onValueChange={field.onChange}
                    disabled={props.isDisabled}
                    className="flex flex-row gap-4 pl-2"
                  >
                    {props.options?.map(o => (
                      <div key={o.value} className="flex gap-2">
                        <RadioGroupItem id={`radiogroupitem-${o.value}`} value={String(o.value)} />
                        <Label className="flex items-center" htmlFor={`radiogroupitem-${o.value}`}>
                          <p className="text-sm font-semibold text-foreground">{o.label}</p>
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                </FormControl>
                <FormMessage />
              </FormItem>
            );
          }}
        />
      );
    }

    if (props.type === 'tel') {
      return (
        <FormField
          control={props.control}
          name={props.name}
          render={({ field }) => (
            <FormItem required={props.required}>
              <FormLabel>{props.label}</FormLabel>
              <FormControl>
                <PhoneInputField
                  placeholder={props.placeholder}
                  type={props.type}
                  {...(field as any)}
                  disabled={props.isDisabled}
                  aria-disabled={props.isDisabled}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      );
    }

    return (
      <FormField
        control={props.control}
        name={props.name}
        render={({ field }) => (
          <FormItem required={props.required} className="p-[1px]">
            <FormLabel>{props.label}</FormLabel>
            <FormControl>
              <Input
                placeholder={props.placeholder}
                type={props.type}
                {...(field as any)}
                disabled={props.isDisabled}
                aria-disabled={props.isDisabled}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    );
  }, [
    props.control,
    props.isDisabled,
    props.required,
    props.label,
    props.name,
    props.options,
    props.placeholder,
    props.type,
  ]);

  return (
    <div
      className={cn('col-span-12 md:col-span-6', {
        ['md:col-span-12']: props.isFullWidth,
      })}
    >
      {content}
    </div>
  );
}

export const profileEditSchema = (VM: FormValidationMessages) =>
  z.object({
    name: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    surname: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    phone: z.string().optional(),
    email: z.string().email(VM.EMAIL_INVALID_VALIDATION_MESSAGE()),
    username: z.string().optional(),
    address: z.string().optional(),
    street_number: z.string().optional(),
    postcode: z.string().optional(),
    city: z.string().optional(),
    country: z
      .object({
        id: z.string().or(z.number()).optional(),
      })
      .optional(),
    company_buy: z.boolean().optional(),
    company: z.string().optional(),
    ico: z.string().optional(),
    dic: z.string().optional(),
    ic_dph: z.string().optional(),
    gender: z
      .object({
        id: z.string().or(z.number()).optional(),
      })
      .optional(),
    image: z
      .object({
        id: z.number(),
      })
      .optional(),
    address_temporary: z.string().optional(),
    street_number_temporary: z.string().optional(),
    postcode_temporary: z.string().optional(),
    city_temporary: z.string().optional(),
    country_temporary: z
      .object({
        id: z.string(),
      })
      .optional(),
    email_company: z.string().email().optional(),
    address_company: z.string().optional(),
    street_number_company: z.string().optional(),
    postcode_company: z.string().optional(),
    city_company: z.string().optional(),
    country_company: z
      .object({
        id: z.string(),
      })
      .optional(),
    address_delivery: z.string().optional(),
    street_number_delivery: z.string().optional(),
    postcode_delivery: z.string().optional(),
    city_delivery: z.string().optional(),
    country_delivery: z
      .object({
        id: z.string(),
      })
      .optional(),
    name_delivery: z.string().optional(),
    surname_delivery: z.string().optional(),
    company_delivery: z.string().optional(),
    iban: z.string().optional(),
    swift: z.string().optional(),
    instagram: z.string().optional(),
    facebook: z.string().optional(),
    national_id_number: z.string().optional(),
  });
